import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/styles'
import ConfirmationDialog from 'components/confirmation-dialog'
import EnhancedTable from 'components/tables/enhanced-table'
import { RefetchContext } from 'lib/utils/refetchContext'
import { DateTime } from 'luxon'
import { REVOKE_GROUP_EVENT_REGISTRATION_INVITE } from './queries'

const useStyles = makeStyles(theme => ({
  actionCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  revokeButton: {
    color: theme.palette.error.main,
    textTransform: 'none',
    whiteSpace: 'nowrap',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '12px',
    lineHeight: '16px',
  },
}))

const columns = [
  {
    id: 'name',
    label: 'Name',
    align: 'left',
    width: '40%',
    mobile: true,
    sortable: false,
  },
  {
    id: 'status',
    label: 'Status',
    align: 'left',
    width: '35%',
    mobile: true,
    sortable: false,
  },
  {
    id: 'actions',
    label: '',
    align: 'right',
    width: '25%',
    mobile: true,
    sortable: false,
  },
]

const formatSentDate = sentAt => DateTime.fromISO(sentAt).toFormat('LLL d')

const RevokeInviteAction = ({ groupEventId, contact }) => {
  const classes = useStyles()
  const refetch = useContext(RefetchContext)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const contactName = `${contact.firstName} ${contact.lastName}`

  const [revokeInvite, { loading }] = useMutation(
    REVOKE_GROUP_EVENT_REGISTRATION_INVITE,
    {
      onCompleted: ({ revokeGroupEventRegistrationInvite }) => {
        setIsConfirmOpen(false)
        if (revokeGroupEventRegistrationInvite.success) {
          setErrorMessage(null)
          refetch?.()
        } else {
          setErrorMessage(revokeGroupEventRegistrationInvite.message)
        }
      },
      onError: () => {
        setIsConfirmOpen(false)
        setErrorMessage('Could not revoke the invite. Please try again.')
      },
    },
  )

  return (
    <Box className={classes.actionCell}>
      <Button
        size="small"
        className={classes.revokeButton}
        onClick={() => setIsConfirmOpen(true)}
      >
        Revoke invitation
      </Button>
      {errorMessage && (
        <Typography className={classes.errorText}>{errorMessage}</Typography>
      )}
      <ConfirmationDialog
        open={isConfirmOpen}
        heading="Revoke invitation?"
        message={`${contactName} will no longer be able to register with the link in their email. They stay on the waitlist, and you can send a new invite later.`}
        confirmText="Revoke"
        disabled={loading}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={() =>
          revokeInvite({ variables: { groupEventId, contactId: contact.id } })
        }
      />
    </Box>
  )
}

const PendingInvitesList = ({ invites, groupEventId }) => {
  const rows = invites.map(invite => (
    <TableRow
      key={invite.id}
      data-testid={`pending-invite-row-${invite.contact.id}`}
    >
      <TableCell>
        <Typography variant="body2" noWrap>
          {`${invite.contact.firstName} ${invite.contact.lastName}`}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="textSecondary" noWrap>
          {invite.sentAt
            ? `Invite sent ${formatSentDate(invite.sentAt)}`
            : 'Invite queued'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <RevokeInviteAction
          groupEventId={groupEventId}
          contact={invite.contact}
        />
      </TableCell>
    </TableRow>
  ))

  return (
    <EnhancedTable
      rows={rows}
      columns={columns}
      label="Pending invites"
      variant="list"
      showHeaderPagination={false}
      showPaginator={false}
      emptyState={() => null}
    />
  )
}

const contactShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
})

RevokeInviteAction.propTypes = {
  groupEventId: PropTypes.string.isRequired,
  contact: contactShape.isRequired,
}

PendingInvitesList.propTypes = {
  groupEventId: PropTypes.string.isRequired,
  invites: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      sentAt: PropTypes.string,
      contact: contactShape.isRequired,
    }),
  ).isRequired,
}

export default PendingInvitesList
